import { useCallback, useState } from "react"
import { Linking, Platform, ScrollView, TextStyle, View, ViewStyle } from "react-native"

import type { LayoutChangeEvent } from "react-native"
import { useFocusEffect } from "expo-router"
import * as ScreenOrientation from "expo-screen-orientation"
import { StatusBar } from "expo-status-bar"
import { useSafeAreaInsets } from "react-native-safe-area-context"

import type { ThemedStyle } from "src/theme"
import { CloseButton } from "src/components/CloseButton"
import { Text } from "src/components/Ignite"
import { SidekickView } from "src/components/SidekickView"
import { useAppTheme } from "src/hooks/useAppTheme"
import { backOrToRoot } from "src/utils/routerHelpers"

const SUPPORT_EMAIL = "support@sidekickplatform.com"

export default function HelpScreen() {
  const insets = useSafeAreaInsets()
  const { themed, navTheme } = useAppTheme()

  const [headerHeight, setHeaderHeight] = useState(60)
  const handleHeaderLayout = useCallback((e: LayoutChangeEvent) => {
    setHeaderHeight(e.nativeEvent.layout.height)
  }, [])

  // Lock to portrait orientation when this screen gains focus
  useFocusEffect(
    useCallback(() => {
      ScreenOrientation.lockAsync(ScreenOrientation.OrientationLock.PORTRAIT_UP).catch(() => {})
    }, []),
  )

  const handleClose = useCallback(() => {
    backOrToRoot()
  }, [])

  return (
    <View style={themed($container)}>
      <StatusBar style={navTheme.dark ? "light" : "dark"} />

      <SidekickView
        showAvatar
        autoShowAvatarOnSpeech
        showCaptions
        showCCToggle
        showButtonCluster
        compactButtonCluster
        darkBackground={true}
        showControlsBarBackground={true}
        style={themed($container)}
        onMenuPress={handleClose}
        riveOwner="video"
        controlsConfig={{ type: "none" }}
      >
        {/* Header */}
        <View
          style={[
            themed($headerContainer),
            {
              top: insets.top,
              left: insets.left,
              right: insets.right,
            },
          ]}
          pointerEvents="box-none"
          onLayout={handleHeaderLayout}
        >
          <Text style={themed($headerTitle)}>Need Help?</Text>
          <CloseButton
            onPress={handleClose}
            accessibilityLabel="Close help"
            style={{ position: "absolute", top: 22, right: 16 }}
          />
        </View>

        {/* Help Content */}
        <ScrollView
          style={$scrollView}
          contentContainerStyle={[
            $scrollContent,
            {
              paddingTop: headerHeight + 10,
              paddingBottom: 40,
            },
          ]}
        >
          <Text style={themed($body)}>
            Your Sidekick can answer questions about how the app works. Just ask using talk or
            type mode. If you still can{"'"}t find what you need, reach out to the Sidekick
            Customer Success team at{" "}
            <Text
              style={themed($emailLink)}
              onPress={() => Linking.openURL(`mailto:${SUPPORT_EMAIL}`)}
            >
              {SUPPORT_EMAIL}
            </Text>
            .
          </Text>
        </ScrollView>
      </SidekickView>
    </View>
  )
}

const $scrollView: ViewStyle = { flex: 1 }

const $scrollContent: ViewStyle = { paddingHorizontal: 24 }

const $container: ThemedStyle<ViewStyle> = ({ colors }) => ({
  flex: 1,
  backgroundColor: colors.background,
})

const $headerContainer: ThemedStyle<ViewStyle> = () => ({
  position: Platform.OS === "web" ? ("fixed" as any) : "absolute",
  zIndex: 2100,
  flexDirection: "row",
  justifyContent: "center",
  alignItems: "center",
  paddingHorizontal: 16,
  height: 80,
  // No backgroundColor — see menu.tsx for rationale (avoid occluding the
  // tab-mode avatar that lives in MediaPlayerShell's foreground layer).
})

const $headerTitle: ThemedStyle<TextStyle> = ({ colors }) => ({
  fontSize: 18,
  fontWeight: "700",
  color: colors.text,
})

const $body: ThemedStyle<TextStyle> = ({ colors }) => ({
  fontSize: 15,
  lineHeight: 24,
  color: colors.text,
  marginBottom: 16,
})

const $emailLink: ThemedStyle<TextStyle> = ({ colors }) => ({
  color: colors.tint,
  textDecorationLine: "underline",
})
